<template>
  <div class="sensitive-word-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">敏感词管理</h2>
      <p class="page-subtitle">管理敏感词库和过滤规则配置</p>
    </div>

    <!-- 模式选择卡片 -->
    <el-card class="mode-selection-card mb-4" shadow="never">
      <template #header>
        <div class="card-header">
          <h3>敏感词检测模式</h3>
          <p>选择敏感词过滤方式</p>
        </div>
      </template>
      
      <el-radio-group v-model="filterMode" size="large" @change="handleModeChange">
        <el-radio label="external" border>
          <div class="radio-content">
            <el-icon class="radio-icon"><Link /></el-icon>
            <div>
              <div class="radio-title">使用外部敏感词服务</div>
              <div class="radio-desc">接入第三方专业敏感词检测API</div>
            </div>
          </div>
        </el-radio>
        <el-radio label="local" border>
          <div class="radio-content">
            <el-icon class="radio-icon"><Folder /></el-icon>
            <div>
              <div class="radio-title">使用本地敏感词库</div>
              <div class="radio-desc">自主维护敏感词列表</div>
            </div>
          </div>
        </el-radio>
      </el-radio-group>
    </el-card>

    <!-- 外部服务配置（仅在选择外部服务时显示） -->
    <el-card 
      v-if="filterMode === 'external'" 
      class="external-service-card mb-4"
      shadow="never"
    >
      <template #header>
        <div class="card-header">
          <h3>外部敏感词服务配置</h3>
          <p>配置第三方敏感词检测服务</p>
        </div>
      </template>
      
      <el-form
        ref="externalFormRef"
        :model="externalConfig"
        :rules="externalRules"
        label-width="140px"
      >
        <el-form-item label="服务供应商" prop="provider">
          <el-input
            v-model="externalConfig.provider"
            placeholder="请输入服务供应商名称（如：阿里云、腾讯云）"
            clearable
            style="width: 400px"
          />
        </el-form-item>
        
        <el-form-item label="API地址" prop="apiUrl">
          <el-input
            v-model="externalConfig.apiUrl"
            placeholder="请输入API地址（如：https://api.example.com/check）"
            clearable
            style="width: 400px"
          />
          <div class="form-tip">
            <el-icon><InfoFilled /></el-icon>
            完整的API接口地址，包含协议和路径
          </div>
        </el-form-item>
        
        <el-form-item label="认证密钥" prop="apiKey">
          <el-input
            v-model="externalConfig.apiKey"
            type="password"
            placeholder="请输入API认证密钥"
            show-password
            clearable
            style="width: 400px"
          />
          <div class="form-tip">
            <el-icon><InfoFilled /></el-icon>
            用于API调用的认证密钥或Token
          </div>
        </el-form-item>
        
        <el-form-item label="超时时间">
          <el-input-number
            v-model="externalConfig.timeout"
            :min="1000"
            :max="30000"
            :step="1000"
            style="width: 150px"
          />
          <span class="form-tip">毫秒（建议：3000-5000）</span>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="testExternalService">
            <el-icon><Connection /></el-icon>
            测试连接
          </el-button>
          <el-button type="success" @click="saveExternalConfig">
            <el-icon><Check /></el-icon>
            保存配置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 本地敏感词库管理（仅在选择本地模式时显示） -->
    <el-card 
      v-if="filterMode === 'local'" 
      class="local-words-card mb-4"
      shadow="never"
    >
      <template #header>
        <div class="card-header">
          <div>
            <h3>本地敏感词库管理</h3>
            <p>维护和管理本地敏感词列表</p>
          </div>
          <el-button type="primary" @click="showAddDialog" size="small">
            <el-icon><Plus /></el-icon>
            添加敏感词
          </el-button>
        </div>
      </template>
      
      <div class="search-container">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索敏感词"
          clearable
          style="width: 300px; margin-right: 10px;"
          @keyup.enter="search"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
        <el-select
          v-model="searchCategory"
          placeholder="选择类别"
          clearable
          style="width: 150px;"
        >
          <el-option
            v-for="category in categories"
            :key="category.value"
            :label="category.label"
            :value="category.value"
          />
        </el-select>
        <el-button type="primary" @click="search" style="margin-left: 10px;">
          搜索
        </el-button>
        <el-button @click="resetSearch" style="margin-left: 10px;">
          重置
        </el-button>
      </div>
      
      <el-table
        v-loading="loading"
        :data="filteredWords"
        style="width: 100%"
        border
        stripe
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="word" label="敏感词" min-width="150" />
        <el-table-column prop="category" label="类别" width="120">
          <template #default="{ row }">
            <el-tag :type="getCategoryTagType(row.category)">
              {{ getCategoryLabel(row.category) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="添加时间" min-width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button
              type="primary"
              @click="showEditDialog(row)"
              size="small"
              link
            >
              编辑
            </el-button>
            <el-button
              type="danger"
              @click="handleDelete(row)"
              size="small"
              link
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalWords"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          background
        />
      </div>
    </el-card>

    <!-- 敏感词拦截记录 -->
    <el-card class="violation-records-card" shadow="never">
      <template #header>
        <div class="card-header">
          <div>
            <h3>敏感词拦截记录</h3>
            <p>查看敏感词检测拦截历史</p>
          </div>
          <el-button @click="refreshViolations" size="small">
            <el-icon><Refresh /></el-icon>
            刷新
          </el-button>
        </div>
      </template>
      
      <el-table
        v-loading="violationsLoading"
        :data="violations"
        style="width: 100%"
        border
        stripe
      >
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="content" label="被拦截内容" min-width="200" show-overflow-tooltip />
        <el-table-column prop="sensitiveWord" label="命中敏感词" width="120">
          <template #default="{ row }">
            <el-tag type="danger" size="small">{{ row.sensitiveWord }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="category" label="类别" width="120">
          <template #default="{ row }">
            <el-tag :type="getCategoryTagType(row.category)">
              {{ getCategoryLabel(row.category) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="contentType" label="内容类型" width="130">
          <template #default="{ row }">
            {{ getContentTypeLabel(row.contentType) }}
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="拦截时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template #default="{ row }">
            <el-button
              type="primary"
              @click="addViolationWordToLocal(row)"
              size="small"
              link
              :disabled="filterMode !== 'local'"
            >
              加入词库
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="violationsPage"
          v-model:page-size="violationsPageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next"
          :total="totalViolations"
          @size-change="handleViolationsSizeChange"
          @current-change="handleViolationsPageChange"
          background
        />
      </div>
    </el-card>

    <!-- 添加/编辑敏感词对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="450px"
      @close="closeDialog"
    >
      <el-form
        ref="wordFormRef"
        :model="wordForm"
        :rules="formRules"
        label-width="80px"
      >
        <el-form-item label="敏感词" prop="word">
          <el-input
            v-model="wordForm.word"
            placeholder="请输入敏感词"
            clearable
          />
        </el-form-item>
        <el-form-item label="类别" prop="category">
          <el-select
            v-model="wordForm.category"
            placeholder="请选择敏感词类别"
            style="width: 100%"
          >
            <el-option
              v-for="category in categories"
              :key="category.value"
              :label="category.label"
              :value="category.value"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="submitWordForm" :loading="submitting">
            确定
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { 
  Plus, Search, Refresh, Link, Folder, Connection, Check, 
  InfoFilled 
} from '@element-plus/icons-vue'
import api from '@/utils/api'

// ==================== 模式选择 ====================
const filterMode = ref('local') // 'external' | 'local'

const handleModeChange = (mode) => {
  console.log('切换到模式:', mode)
  if (mode === 'external') {
    ElMessage.info('已切换到外部服务模式，本地词库编辑功能已禁用')
  } else {
    ElMessage.info('已切换到本地词库模式')
  }
}

// ==================== 外部服务配置 ====================
const externalFormRef = ref(null)
const externalConfig = reactive({
  provider: '',
  apiUrl: '',
  apiKey: '',
  timeout: 5000
})

const externalRules = {
  provider: [
    { required: true, message: '请输入服务供应商名称', trigger: 'blur' }
  ],
  apiUrl: [
    { required: true, message: '请输入API地址', trigger: 'blur' },
    {
      pattern: /^https?:\/\/.+/,
      message: 'API地址格式不正确，应以http://或https://开头',
      trigger: 'blur'
    }
  ],
  apiKey: [
    { required: true, message: '请输入认证密钥', trigger: 'blur' }
  ]
}

// 测试外部服务连接
const testExternalService = async () => {
  if (!externalFormRef.value) return
  
  try {
    await externalFormRef.value.validate()
    
    ElMessage.info('正在测试连接...')
    
    // TODO: 调用真实API测试连接
    // const response = await api.testExternalService(externalConfig)
    
    // 模拟测试
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    ElMessage.success('连接测试成功！')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('连接测试失败，请检查配置')
      console.error('测试外部服务失败:', error)
    }
  }
}

// 保存外部服务配置
const saveExternalConfig = async () => {
  if (!externalFormRef.value) return
  
  try {
    await externalFormRef.value.validate()
    
    // TODO: 调用真实API保存配置
    // const response = await api.saveExternalServiceConfig(externalConfig)
    
    ElMessage.success('外部服务配置保存成功')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('保存配置失败')
      console.error('保存外部服务配置失败:', error)
    }
  }
}

// ==================== 本地敏感词库 ====================
const categories = [
  { value: 'politics', label: '政治敏感' },
  { value: 'pornographic', label: '色情低俗' },
  { value: 'violence', label: '暴力恐怖' },
  { value: 'gambling', label: '赌博相关' },
  { value: 'fraud', label: '诈骗相关' },
  { value: 'other', label: '其他不适宜' }
]

const loading = ref(false)
const sensitiveWords = ref([])
const searchKeyword = ref('')
const searchCategory = ref('')
const currentPage = ref(1)
const pageSize = ref(20)
const totalWords = ref(0)

// 对话框相关
const dialogVisible = ref(false)
const dialogTitle = ref('添加敏感词')
const submitting = ref(false)
const wordFormRef = ref(null)
const wordForm = reactive({
  id: '',
  word: '',
  category: ''
})
const editingWord = ref(null)

// 表单验证规则
const formRules = {
  word: [
    { required: true, message: '请输入敏感词', trigger: 'blur' },
    { min: 1, max: 20, message: '敏感词长度应在 1 到 20 个字符之间', trigger: 'blur' }
  ],
  category: [
    { required: true, message: '请选择敏感词类别', trigger: 'change' }
  ]
}

// 获取所有敏感词
const fetchSensitiveWords = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      limit: pageSize.value,
      search: searchKeyword.value,
      category: searchCategory.value
    }
    const response = await api.getSensitiveWords(params)
    
    if (response.success) {
      sensitiveWords.value = response.data.words
      totalWords.value = response.data.pagination.total
    } else {
      ElMessage.error(response.message || '获取敏感词列表失败')
    }
  } catch (error) {
    ElMessage.error('获取敏感词列表失败')
    console.error('获取敏感词列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 敏感词列表（已由后端过滤和分页）
const filteredWords = computed(() => {
  return sensitiveWords.value
})

// 搜索
const search = () => {
  currentPage.value = 1
  fetchSensitiveWords()
}

// 重置搜索
const resetSearch = () => {
  searchKeyword.value = ''
  searchCategory.value = ''
  currentPage.value = 1
  fetchSensitiveWords()
}

// 显示添加敏感词对话框
const showAddDialog = () => {
  dialogTitle.value = '添加敏感词'
  Object.keys(wordForm).forEach(key => {
    wordForm[key] = ''
  })
  editingWord.value = null
  dialogVisible.value = true
}

// 显示编辑敏感词对话框
const showEditDialog = (row) => {
  dialogTitle.value = '编辑敏感词'
  Object.assign(wordForm, row)
  editingWord.value = row
  dialogVisible.value = true
}

// 关闭对话框
const closeDialog = () => {
  dialogVisible.value = false
  if (wordFormRef.value) {
    wordFormRef.value.resetFields()
  }
}

// 提交敏感词表单
const submitWordForm = async () => {
  if (!wordFormRef.value) return
  
  try {
    await wordFormRef.value.validate()
    
    submitting.value = true
    
    if (editingWord.value) {
      await updateSensitiveWord()
    } else {
      await addSensitiveWord()
    }
  } catch (error) {
    console.error('表单验证失败:', error)
  } finally {
    submitting.value = false
  }
}

// 添加敏感词
const addSensitiveWord = async () => {
  try {
    const response = await api.addSensitiveWord(wordForm)
    
    if (response.success) {
      ElMessage.success('敏感词添加成功')
      closeDialog()
      // 刷新敏感词列表
      fetchSensitiveWords()
    } else {
      ElMessage.error(response.message || '敏感词添加失败')
    }
  } catch (error) {
    ElMessage.error('敏感词添加失败')
    console.error('敏感词添加失败:', error)
  }
}

// 更新敏感词
const updateSensitiveWord = async () => {
  try {
    const response = await api.updateSensitiveWord(wordForm.id, {
      word: wordForm.word,
      category: wordForm.category
    })
    
    if (response.success) {
      // 更新本地列表
      const index = sensitiveWords.value.findIndex(word => word.id === wordForm.id)
      if (index !== -1) {
        sensitiveWords.value[index] = { ...wordForm }
      }
      
      ElMessage.success('敏感词更新成功')
      closeDialog()
    } else {
      ElMessage.error(response.message || '敏感词更新失败')
    }
  } catch (error) {
    ElMessage.error('敏感词更新失败')
    console.error('敏感词更新失败:', error)
  }
}

// 删除敏感词
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除敏感词 "${row.word}" 吗？`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    loading.value = true
    const response = await api.deleteSensitiveWord(row.id)
    
    if (response.success) {
      const index = sensitiveWords.value.findIndex(word => word.id === row.id)
      if (index !== -1) {
        sensitiveWords.value.splice(index, 1)
        totalWords.value--
      }
      ElMessage.success('敏感词删除成功')
    } else {
      ElMessage.error(response.message || '敏感词删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('敏感词删除失败')
      console.error('敏感词删除失败:', error)
    }
  } finally {
    loading.value = false
  }
}

// 分页大小变化
const handleSizeChange = (size) => {
  pageSize.value = size
  fetchSensitiveWords()
}

// 当前页码变化
const handleCurrentChange = (current) => {
  currentPage.value = current
  fetchSensitiveWords()
}

// ==================== 违规记录 ====================
const violationsLoading = ref(false)
const violations = ref([])
const violationsPage = ref(1)
const violationsPageSize = ref(10)
const totalViolations = ref(0)

// 获取违规记录
const fetchViolations = async () => {
  violationsLoading.value = true
  try {
    const params = {
      page: violationsPage.value,
      limit: violationsPageSize.value
    }
    const response = await api.getContentViolations(params)
    
    if (response.success) {
      violations.value = response.data.violations || response.data.records || []
      totalViolations.value = response.data.pagination?.total || violations.value.length
    } else {
      ElMessage.error(response.message || '获取违规记录失败')
    }
  } catch (error) {
    ElMessage.error('获取违规记录失败')
    console.error('获取违规记录失败:', error)
  } finally {
    violationsLoading.value = false
  }
}

// 刷新违规记录
const refreshViolations = () => {
  fetchViolations()
}

// 违规记录分页
const handleViolationsSizeChange = (size) => {
  violationsPageSize.value = size
  fetchViolations()
}

const handleViolationsPageChange = (page) => {
  violationsPage.value = page
  fetchViolations()
}

// 将违规词添加到本地词库
const addViolationWordToLocal = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要将 "${row.sensitiveWord}" 添加到本地敏感词库吗？`,
      '添加确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }
    )
    
    // 检查是否已存在（可选，后端可能也会检查）
    const exists = sensitiveWords.value.some(w => w.word === row.sensitiveWord)
    if (exists) {
      ElMessage.warning('该敏感词已存在于词库中')
      return
    }
    
    const response = await api.addViolationToLocal({
      word: row.sensitiveWord,
      category: row.category
    })
    
    if (response.success) {
      // 刷新敏感词列表以获取最新的词库
      fetchSensitiveWords()
      ElMessage.success('已成功添加到本地敏感词库')
    } else {
      ElMessage.error(response.message || '添加失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('添加失败')
      console.error('添加到本地词库失败:', error)
    }
  }
}

// ==================== 工具函数 ====================
const getCategoryLabel = (categoryValue) => {
  const category = categories.find(cat => cat.value === categoryValue)
  return category ? category.label : categoryValue
}

const getCategoryTagType = (categoryValue) => {
  const typeMap = {
    'politics': 'danger',
    'pornographic': 'warning',
    'violence': 'danger',
    'gambling': 'warning',
    'fraud': 'danger',
    'other': 'info'
  }
  return typeMap[categoryValue] || 'info'
}

const getContentTypeLabel = (contentType) => {
  const labelMap = {
    'activity_title': '活动标题',
    'activity_description': '活动描述',
    'user_nickname': '用户昵称',
    'user_signature': '用户签名',
    'feedback_content': '反馈内容',
    'comment_content': '评论内容'
  }
  return labelMap[contentType] || contentType
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return dateString
}

// ==================== 生命周期 ====================
onMounted(() => {
  fetchSensitiveWords()
  fetchViolations()
})
</script>

<style scoped>
.sensitive-word-management {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 600;
  color: #303133;
}

.page-subtitle {
  margin: 0;
  font-size: 14px;
  color: #909399;
}

.mb-4 {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.card-header p {
  margin: 0;
  font-size: 12px;
  color: #909399;
}

/* 模式选择样式 */
.mode-selection-card :deep(.el-radio-group) {
  display: flex;
  gap: 16px;
  width: 100%;
}

.mode-selection-card :deep(.el-radio.is-bordered) {
  flex: 1;
  padding: 20px;
  margin: 0;
  border-width: 2px;
  transition: all 0.3s;
}

.mode-selection-card :deep(.el-radio.is-bordered.is-checked) {
  border-color: #409eff;
  background-color: #ecf5ff;
}

.radio-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.radio-icon {
  font-size: 24px;
  color: #409eff;
}

.radio-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.radio-desc {
  font-size: 12px;
  color: #909399;
}

/* 外部服务配置 */
.form-tip {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

/* 搜索容器 */
.search-container {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 8px;
}

/* 分页 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 对话框 */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* 响应式 */
@media (max-width: 768px) {
  .mode-selection-card :deep(.el-radio-group) {
    flex-direction: column;
  }
  
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-container .el-input,
  .search-container .el-select {
    width: 100% !important;
  }
}
</style>
